import { getDb } from './db';
import { repairBudgetCategories } from './repair-categories';
const VERSION_KEY = 'dbm_version';
const HISTORY_KEY = 'dbm_history';
const migrations = [
    {
        version: 'dbm0.0.1',
        description: 'Baseline — record initial data model version',
        migrate: async () => { },
    },
    {
        version: 'dbm0.0.2',
        description: 'Move showNextMonth from Budget to UserProfile; add fromSavings, seriesId to Transaction',
        migrate: async (db) => {
            const profileRow = await db.get('meta', 'profile');
            const profile = profileRow?.data;
            const budgets = await db.getAll('budgets');
            let showNextMonth;
            for (const b of budgets) {
                const raw = b;
                if ('showNextMonth' in raw) {
                    showNextMonth = raw.showNextMonth;
                    break;
                }
            }
            if (profile && !('showNextMonth' in profile) && showNextMonth !== undefined) {
                profile.showNextMonth = showNextMonth;
                await db.put('meta', { key: 'profile', data: profile });
            }
            const tx = db.transaction('budgets', 'readwrite');
            const store = tx.objectStore('budgets');
            for (const b of budgets) {
                const raw = b;
                if ('showNextMonth' in raw) {
                    delete raw.showNextMonth;
                    await store.put(raw);
                }
            }
            await tx.done;
        },
    },
    {
        version: 'dbm0.0.3',
        description: 'Add isArchived flag to budgets',
        migrate: async (db) => {
            const budgets = await db.getAll('budgets');
            const tx = db.transaction('budgets', 'readwrite');
            const store = tx.objectStore('budgets');
            for (const b of budgets) {
                const raw = b;
                if (!('isArchived' in raw)) {
                    raw.isArchived = false;
                    await store.put(raw);
                }
            }
            await tx.done;
        },
    },
    {
        version: 'dbm0.0.4',
        description: 'Add version and storageMode fields to budgets/categories/transactions',
        migrate: async (db) => {
            const tx = db.transaction('budgets', 'readwrite');
            const store = tx.objectStore('budgets');
            let cursor = await store.openCursor();
            while (cursor) {
                const budget = cursor.value;
                const raw = budget;
                if (raw.version === undefined)
                    raw.version = 1;
                if (raw.storageMode === undefined)
                    raw.storageMode = 'LocalOnly';
                const categories = raw.categories || [];
                for (const cat of categories) {
                    if (cat.version === undefined)
                        cat.version = 1;
                }
                const transactions = raw.transactions || [];
                for (const txn of transactions) {
                    if (txn.version === undefined)
                        txn.version = 1;
                }
                await cursor.update(budget);
                cursor = await cursor.continue();
            }
            await tx.done;
        },
    },
    {
        version: 'dbm0.0.5',
        description: 'Convert non-UUID ids to crypto.randomUUID() for sync compatibility',
        migrate: async (db) => {
            const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            const isUuid = (v) => uuidRe.test(v);
            const budgets = await db.getAll('budgets');
            if (budgets.length === 0)
                return;
            const tx = db.transaction(['budgets', 'outbox'], 'readwrite');
            const budgetStore = tx.objectStore('budgets');
            const outboxStore = tx.objectStore('outbox');
            // Clear stale outbox entries — they reference old ids
            let outboxCursor = await outboxStore.openCursor();
            while (outboxCursor) {
                await outboxCursor.delete();
                outboxCursor = await outboxCursor.continue();
            }
            for (const budget of budgets) {
                const oldBudgetId = budget.id;
                const categoryIdMap = new Map();
                // Remap category ids
                for (const cat of budget.categories) {
                    if (!isUuid(cat.id)) {
                        const newId = crypto.randomUUID();
                        categoryIdMap.set(cat.id, newId);
                        cat.id = newId;
                    }
                }
                // Remap transaction ids + category references
                for (const txn of budget.transactions) {
                    if (!isUuid(txn.id)) {
                        txn.id = crypto.randomUUID();
                    }
                    if (txn.category && categoryIdMap.has(txn.category)) {
                        txn.category = categoryIdMap.get(txn.category);
                    }
                }
                // Remap budget id
                if (!isUuid(oldBudgetId)) {
                    budget.id = crypto.randomUUID();
                    await budgetStore.delete(oldBudgetId);
                }
                await budgetStore.put(budget);
            }
            await tx.done;
        },
    },
    {
        version: 'dbm0.0.6',
        description: 'Repair broken/duplicate category ids (legacy string ids + "c"-prefixed GUIDs)',
        migrate: async (db) => {
            const budgets = await db.getAll('budgets');
            if (budgets.length === 0)
                return;
            const tx = db.transaction(['budgets', 'outbox'], 'readwrite');
            const budgetStore = tx.objectStore('budgets');
            const outboxStore = tx.objectStore('outbox');
            // Полная очистка outbox: pending-операции ссылаются на старые (битые) id и всё равно junk;
            // сервер authoritative, следующий pull подтянет актуальное состояние без malformed-операций.
            let outboxCursor = await outboxStore.openCursor();
            while (outboxCursor) {
                await outboxCursor.delete();
                outboxCursor = await outboxCursor.continue();
            }
            // Чиним категории каждого бюджета: дедуп по контенту (перенимаем серверный GUID близнеца),
            // свежий GUID для битых без близнеца, перепривязка транзакций.
            for (const budget of budgets) {
                repairBudgetCategories(budget);
                await budgetStore.put(budget);
            }
            await tx.done;
        },
    },
];
function parseVersion(v) {
    const nums = v.replace(/^dbm/, '').split('.').map(Number);
    return [nums[0], nums[1], nums[2]];
}
function compareVersions(a, b) {
    const pa = parseVersion(a);
    const pb = parseVersion(b);
    for (let i = 0; i < 3; i++) {
        if (pa[i] !== pb[i])
            return pa[i] - pb[i];
    }
    return 0;
}
export async function getDbmVersion() {
    const db = await getDb();
    const row = await db.get('meta', VERSION_KEY);
    return row ? row.data : null;
}
export async function runMigrations() {
    const db = await getDb();
    const versionRow = await db.get('meta', VERSION_KEY);
    const currentVersion = versionRow ? versionRow.data : null;
    const historyRow = await db.get('meta', HISTORY_KEY);
    const history = historyRow ? historyRow.data : [];
    const pending = currentVersion === null
        ? migrations
        : migrations.filter((m) => compareVersions(m.version, currentVersion) > 0);
    if (pending.length === 0)
        return;
    for (const migration of pending) {
        await migration.migrate(db);
        const tx = db.transaction('meta', 'readwrite');
        const store = tx.objectStore('meta');
        history.push({
            version: migration.version,
            appliedAt: new Date().toISOString(),
        });
        await store.put({ key: HISTORY_KEY, data: history });
        await store.put({ key: VERSION_KEY, data: migration.version });
        await tx.done;
    }
}
